import { all, takeEvery, take, select, put } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'react-router-redux';

import routes from '../helpers/ApplicationRoutes';

import * as organizationSelectors from '../selectors/organizationSelectors';
import * as userSelectors from '../selectors/userSelectors';
import * as bucketSelectors from '../selectors/bucketSelectors';
import * as bucketMembersSelectors from '../selectors/bucketMembersSelectors';
import * as organizationMembersSelectors from '../selectors/organizationMembersSelectors';
import * as organizationInvitationSelector from '../selectors/organizationInvitationsSelector';

import * as userConstants from '../constants/user';
import * as organizationConstants from '../constants/organizations';
import * as assetConstants from '../constants/assets';
import * as bucketConstants from '../constants/buckets';

/**
 * Triggers fetch organization buckets if on appropriate path
 * @param {String} pathname current application path
 */
function* fetchBuckets(pathname) {
    const isAnyBucketPath = routes.bucket.isPath(pathname);
    const isOrganizationBucketsPath = routes.organizationBuckets.isExactPath(
        pathname,
    );
    const isReportPage = routes.organizationReport.isExactPath(pathname);
    const isMembersPage = routes.organizationMembers.isExactPath(pathname);
    if (isOrganizationBucketsPath || isReportPage || isMembersPage) {
        const organizationId = yield select(
            organizationSelectors.getCurrentOrganizationId,
        );
        const isLoadingBuckets = yield select(
            bucketSelectors.getIsLoadingBuckets,
        );

        if (!isLoadingBuckets) {
            yield put({
                type: organizationConstants.FETCH_ORGANIZATION_BUCKETS_REQUEST,
                payload: {
                    organizationId,
                    resetList: true,
                    fetchAll: isReportPage,
                },
            });
        }
    } else if (isAnyBucketPath) {
        const bucketId = yield select(bucketSelectors.getCurrentBucketId);
        const isLoadingBuckets = yield select(
            bucketSelectors.getIsLoadingBuckets,
        );
        const organizationId = yield select(
            organizationSelectors.getCurrentOrganizationId,
        );
        if (!isLoadingBuckets) {
            yield put({
                type: organizationConstants.FETCH_BUCKET_REQUEST,
                payload: {
                    bucketId,
                    organizationId,
                },
            });
        }
    }
}

/**
 * Triggers fetch bucket members if on appropriate path
 * @param {String} pathname current application path
 */
function* fetchCurrentBucketMembers(pathname) {
    if (routes.bucket.isPath(pathname)) {
        const isLoadingMembers = yield select(
            bucketMembersSelectors.getIsLoadingMembers,
        );
        const members = yield select(bucketMembersSelectors.getMembers);
        const membersError = yield select(bucketMembersSelectors.getError);
        if (!isLoadingMembers && !members && !membersError) {
            yield put({
                type: bucketConstants.FETCH_CURRENT_BUCKET_MEMBERS_REQUEST,
            });
        }
    }
}

/**
 * Triggers fetch organization members if on appropriate path
 * @param {String} pathname current application path
 */
function* fetchOrganizationMembers(pathname) {
    const isBucketMembersPath = routes.bucketMembers.isExactPath(pathname);
    const isOrganizationMembersPath = routes.organizationMembers.isExactPath(
        pathname,
    );
    if (isBucketMembersPath || isOrganizationMembersPath) {
        const organizationId = yield select(
            organizationSelectors.getCurrentOrganizationId,
        );
        const isLoadingMembers = yield select(
            organizationMembersSelectors.getIsLoadingMembers,
        );
        const members = yield select(organizationMembersSelectors.getMembers);
        if (!isLoadingMembers && !members) {
            yield put({
                type: organizationConstants.FETCH_ORGANIZATION_MEMBERS_REQUEST,
                payload: { organizationId },
            });
        }
    }
}

/**
 * Triggers fetch organization invitation if on appropriate path
 * @param {String} pathname current application path
 */
function* fetchOrganizationInvitations(pathname) {
    if (routes.organizationMembers.isExactPath(pathname)) {
        const organizationId = yield select(
            organizationSelectors.getCurrentOrganizationId,
        );
        const isLoadingInvitations = yield select(
            organizationInvitationSelector.getIsLoadingInvitations,
        );
        const invitations = yield select(
            organizationInvitationSelector.getInvitations,
        );
        if (!isLoadingInvitations && !invitations) {
            yield put({
                type:
                    organizationConstants.FETCH_ORGANIZATION_MEMBER_INVITATIONS_REQUEST,
                payload: { organizationId },
            });
        }
    }
}

/**
 * Triggers fetch bucket assets if on appropriate path
 * @param {String} pathname current application path
 */
function* fetchBucketAssets(pathname) {
    if (routes.bucketAssets.isExactPath(pathname)) {
        const params = routes.bucketAssets.getParams(pathname);
        yield put({
            type: assetConstants.FETCH_ASSETS_REQUEST,
            payload: {
                bucketId: +params.bucketId,
                application: params.application,
            },
        });
    }
}

/**
 * Loads data from server depending current application path
 * @param {Object} action Payload passed on every location change.
 */
function* fetchData(action) {
    const isLoadingCurrentUser = yield select(
        userSelectors.getIsLoadingCurrentUser,
    );
    if (isLoadingCurrentUser) {
        yield take(userConstants.FETCH_ME_SUCCESS);
    }
    const isLoadingOrganization = yield select(
        organizationSelectors.getIsLoadingOrganizations,
    );
    if (isLoadingOrganization) {
        yield take(organizationConstants.FETCH_ORGANIZATIONS_SUCCESS);
    }

    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );

    if (!organizationId) {
        return;
    }

    const { pathname } = action.payload;

    // fetch data depending on what page in application we are
    yield fetchOrganizationMembers(pathname);
    yield fetchOrganizationInvitations(pathname);
    yield fetchBuckets(pathname);
    yield fetchCurrentBucketMembers(pathname);
    yield fetchBucketAssets(pathname);
}

function* watchRouteChange() {
    yield takeEvery(LOCATION_CHANGE, fetchData);
}

export default function* routeSagas() {
    yield all([watchRouteChange()]);
}



// WEBPACK FOOTER //
// ./src/sagas/routeSagas.js